import React, { useState, useEffect } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Progress } from '@nextui-org/react';
import { AlertTriangle, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import healthService from '../services/healthService';

interface MaintenanceOverlayProps {
  isOpen: boolean;
  onRetry?: () => void;
}

const MaintenanceOverlay: React.FC<MaintenanceOverlayProps> = ({ isOpen, onRetry }) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryAttempts, setRetryAttempts] = useState(0);
  const [lastCheck, setLastCheck] = useState<number>(0);
  const [timeUntilNextCheck, setTimeUntilNextCheck] = useState(10);

  useEffect(() => {
    setLastCheck(healthService.getLastHealthCheck());
  }, [isOpen]);

  // Countdown Timer für nächsten Health Check
  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastCheck = now - lastCheck;
      const remaining = Math.max(0, Math.ceil((10000 - timeSinceLastCheck) / 1000));
      setTimeUntilNextCheck(remaining);

      if (remaining === 0) {
        setLastCheck(now);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, lastCheck]);

  const handleRetry = async () => {
    if (isRetrying) return;
    
    setIsRetrying(true);
    setRetryAttempts(prev => prev + 1);

    try {
      const success = await healthService.checkHealthWithRetry(3);
      if (success && onRetry) {
        onRetry();
      }
    } catch (error) {
      console.error('Manual retry failed:', error);
    } finally {
      setIsRetrying(false);
    }
  };

  const getStatusIcon = () => {
    if (isRetrying) {
      return <Spinner size="sm" className="text-yellow-500" />;
    }
    return <WifiOff size={24} className="text-red-500" />;
  };

  const formatLastCheckTime = () => {
    if (lastCheck === 0) return 'Noch nicht geprüft';
    
    const secondsAgo = Math.floor((Date.now() - lastCheck) / 1000);
    if (secondsAgo < 60) return `vor ${secondsAgo} Sekunden`;
    if (secondsAgo < 3600) return `vor ${Math.floor(secondsAgo / 60)} Minuten`;
    return `vor ${Math.floor(secondsAgo / 3600)} Stunden`;
  };

  return (
    <Modal
      isOpen={isOpen}
      hideCloseButton
      isDismissable={false}
      isKeyboardDismissDisabled
      size="md"
      placement="center"
      scrollBehavior="inside"
      classNames={{
        backdrop: "bg-black/80 backdrop-blur-sm",
        // max-h + inside-Scroll: auf kleinen Screens wurde das Modal sonst
        // oben abgeschnitten (Inhalt höher als der Viewport)
        base: "bg-zinc-900 border border-zinc-800 max-h-[92dvh] mx-3",
        header: "border-b border-zinc-800",
        body: "py-4 sm:py-6",
        footer: "border-t border-zinc-800"
      }}
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-3">
          {getStatusIcon()}
          <div>
            <h3 className="text-lg font-bold text-white">Wartungsarbeiten</h3>
            <p className="text-sm text-gray-400 font-normal">
              Backend derzeit nicht erreichbar
            </p>
          </div>
        </ModalHeader>

        <ModalBody>
          <div className="space-y-4">
            {/* Hauptnachricht */}
            <div className="text-center py-2 sm:py-4">
              <AlertTriangle size={40} className="mx-auto text-yellow-500 mb-3" />
              <p className="text-gray-300 mb-2">
                Unsere Server sind momentan nicht erreichbar. Dies kann verschiedene Gründe haben:
              </p>
              <ul className="text-sm text-gray-400 text-left max-w-sm mx-auto space-y-1">
                <li>• Planmäßige Wartungsarbeiten</li>
                <li>• Temporäre Netzwerkprobleme</li>
                <li>• Server-Updates werden durchgeführt</li>
              </ul>
            </div>

            {/* Status Informationen */}
            <div className="bg-zinc-800 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-400">Status:</span>
                <span className="text-red-400 font-medium">Offline</span>
              </div>
              
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-400">Letzte Prüfung:</span>
                <span className="text-gray-300">{formatLastCheckTime()}</span>
              </div>

              {retryAttempts > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400">Versuche:</span>
                  <span className="text-gray-300">{retryAttempts}</span>
                </div>
              )}

              {/* Countdown bis zur nächsten automatischen Prüfung */}
              <div className="space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400">Nächste Prüfung in:</span>
                  <span className="text-[#3fced1] font-medium">{timeUntilNextCheck}s</span>
                </div>
                <Progress
                  value={((10 - timeUntilNextCheck) / 10) * 100}
                  className="w-full"
                  classNames={{
                    indicator: "bg-[#3fced1]"
                  }}
                />
              </div>
            </div>

            {/* Hilfetext */}
            <div className="text-center text-sm text-gray-500">
              <p>
                Die Verbindung wird automatisch alle 10 Sekunden überprüft.
                Sobald der Server wieder erreichbar ist, wird die Anwendung automatisch fortgesetzt.
              </p>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            fullWidth
            variant="bordered"
            className="border-[#3fced1] text-[#3fced1] hover:bg-[#3fced1] hover:text-black"
            onPress={handleRetry}
            isLoading={isRetrying}
            startContent={!isRetrying && <RefreshCw size={16} />}
          >
            {isRetrying ? 'Verbindung wird geprüft...' : 'Jetzt erneut versuchen'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MaintenanceOverlay;